import axios from "../../api/axiosInstance";
import { STATUS, getProjectId, extractErrorInfo } from "./sharedfunction";

/**
 * Get multiple attachments by their IDs
 * POST /:projectId/attachment/getDetails
 * @param {Object} params - Parameters
 * @param {Array<string>} params.attachmentIds - Array of attachment IDs (UUIDs)
 * @returns {Promise<{status: number, data?: Array, error?: string, message?: string}>}
 */
export async function getAttachmentsByIds({ attachmentIds } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!attachmentIds || !Array.isArray(attachmentIds) || attachmentIds.length === 0) {
      return {
        status: STATUS.ERROR,
        error: "At least 1 Attachment ID is required",
      };
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/attachment/getDetails`,
      { attachmentIds },
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || [],
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching attachments by IDs:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Get a single attachment by its ID
 * GET /:projectId/attachment/getDetail?id=xxx
 * @param {Object} params - Parameters
 * @param {string} params.id - Attachment ID (UUID)
 * @returns {Promise<{status: number, data?: Object, error?: string, message?: string}>}
 */
export async function getAttachmentById({ id } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!id) {
      return {
        status: STATUS.ERROR,
        error: "Attachment ID is required",
      };
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/attachment/getDetail`,
      {
        params: { id },
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching attachment by ID:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Delete one or more attachments by their IDs
 * DELETE /:projectId/attachment/delete
 * @param {Object} params - Parameters
 * @param {Array<string>} params.attachmentIds - Array of attachment IDs (UUIDs) to delete
 * @returns {Promise<{status: number, data?: Object, error?: string, message?: string}>}
 */
export async function deleteAttachments({ attachmentIds } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!attachmentIds || !Array.isArray(attachmentIds) || attachmentIds.length === 0) {
      return {
        status: STATUS.ERROR,
        error: "At least 1 Attachment ID is required",
      };
    }

    const response = await axios.delete(
      `${process.env.REACT_APP_API_URL}/${projectId}/attachment/delete`,
      {
        data: { attachmentIds },
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error deleting attachments:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Upload one or more files as attachments
 * POST /:projectId/attachment/upload
 * @param {Object} params - Parameters
 * @param {Array<File>} params.files - Array of files to upload (max 10 files)
 * @param {string} params.uploaded_by - User ID who is uploading the files (UUID)
 * @returns {Promise<{status: number, data?: Array, error?: string, message?: string}>}
 */
export async function uploadAttachment({ files, uploaded_by } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!files || files.length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Files are required for upload",
      };
    }

    if (files.length > 10) {
      return {
        status: STATUS.ERROR,
        error: "Maximum 10 files can be uploaded at once",
      };
    }

    if (!uploaded_by) {
      return {
        status: STATUS.ERROR,
        error: "uploaded_by (User ID) is required",
      };
    }

    const formData = new FormData();
    
    // Append files
    Array.from(files).forEach((file) => {
      formData.append('files', file);
    });
    
    // Append user ID
    formData.append('uploaded_by', uploaded_by);

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/attachment/upload`,
      formData,
      {
        headers: { 
          'Content-Type': 'multipart/form-data' 
        },
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || [],
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error uploading attachments:", error);
    return extractErrorInfo(error);
  }
}

